import type { SormEntityName } from '@moment/sorm-client-data-access/schema'

interface ViewsEntityDefinition {
  /** SORM root entity this view queries. */
  sormEntity: SormEntityName
  /** Plural label for the entity switcher and breadcrumb. */
  label: string
  /** Placeholder for the list's free-text search box. */
  searchPlaceholder: string
}

const ACCOUNT_SEARCH_PLACEHOLDER = 'Search account name or ID'
const SLEEVE_SEARCH_PLACEHOLDER = 'Search sleeve name or ID'
const SECURITY_SEARCH_PLACEHOLDER = 'Search CUSIP, ticker, or issuer name'

/**
 * Every entity the Views page can render, keyed by its `?entity=` URL param.
 *
 * This table is the whole registration: the `ClientListDetailEntity` union, the
 * nuqs param allowlist, the switcher options, the breadcrumb label, the search
 * placeholder, the SORM root entity and the icon requirement all derive from it.
 * An entity is therefore either fully registered here or absent from Views
 * entirely — it cannot be half-registered into a pill that renders and does
 * nothing on click. That bug landed three times while this enumeration lived in
 * eight hand-written maps: `CashRaise` (#8685/#8709), `WashSaleRestriction`
 * (#9621), and `CashRaiseSchedule` (AT-2070).
 *
 * A SORM entity absent from this table is not offered on Views. `Portfolio`,
 * `RegistrySchema` and `RegistrySchemaField` are the live examples: they are
 * queryable SORM entities that Views has no grid for.
 */
export const VIEWS_ENTITIES = {
  account: {
    sormEntity: 'Account',
    label: 'Accounts',
    searchPlaceholder: ACCOUNT_SEARCH_PLACEHOLDER,
  },
  streaming_account: {
    sormEntity: 'StreamingAccount',
    label: 'Streaming Accounts',
    searchPlaceholder: ACCOUNT_SEARCH_PLACEHOLDER,
  },
  position: {
    sormEntity: 'Position',
    label: 'Positions',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  streaming_position: {
    sormEntity: 'StreamingPosition',
    label: 'Streaming Positions',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  lot: {
    sormEntity: 'Lot',
    label: 'Lots',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  streaming_lot: {
    sormEntity: 'StreamingLot',
    label: 'Streaming Lots',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  security: {
    sormEntity: 'Security',
    label: 'Securities',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  sleeve: {
    sormEntity: 'Sleeve',
    label: 'Sleeves',
    searchPlaceholder: SLEEVE_SEARCH_PLACEHOLDER,
  },
  streaming_sleeve: {
    sormEntity: 'StreamingSleeve',
    label: 'Streaming Sleeves',
    searchPlaceholder: SLEEVE_SEARCH_PLACEHOLDER,
  },
  sleeve_constraint: {
    sormEntity: 'SleeveConstraint',
    label: 'Sleeve Constraints',
    searchPlaceholder: 'Search sleeve constraints',
  },
  sleeve_constraint_violation: {
    sormEntity: 'SleeveConstraintViolation',
    label: 'Sleeve Constraint Violations',
    searchPlaceholder: 'Search sleeve constraint violations',
  },
  proposal: {
    sormEntity: 'Proposal',
    label: 'Proposals',
    searchPlaceholder: 'Search proposals',
  },
  proposal_position: {
    sormEntity: 'ProposalPosition',
    label: 'Proposal Positions',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  proposal_lot: {
    sormEntity: 'ProposalLot',
    label: 'Proposal Lots',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  proposal_order: {
    sormEntity: 'ProposalOrder',
    label: 'Proposal Orders',
    searchPlaceholder: SECURITY_SEARCH_PLACEHOLDER,
  },
  transaction: {
    sormEntity: 'Transaction',
    label: 'Transactions',
    searchPlaceholder: 'Search transactions or account',
  },
  order: {
    sormEntity: 'Order',
    label: 'Orders',
    searchPlaceholder: 'Search orders',
  },
  execution: {
    sormEntity: 'Execution',
    label: 'Executions',
    searchPlaceholder: 'Search executions',
  },
  execution_order: {
    sormEntity: 'ExecutionOrder',
    label: 'Execution Orders',
    searchPlaceholder: 'Search execution orders',
  },
  allocation: {
    sormEntity: 'AccountAllocation',
    label: 'Allocations',
    searchPlaceholder: 'Search allocations',
  },
  best_execution_order: {
    sormEntity: 'BestExecutionOrder',
    label: 'Best Execution Orders',
    searchPlaceholder: 'Search best execution orders',
  },
  trade_plan: {
    sormEntity: 'TradePlan',
    label: 'Trade Plans',
    searchPlaceholder: 'Search trade plans',
  },
  cash_raise: {
    sormEntity: 'CashRaise',
    label: 'Cash Raises',
    searchPlaceholder: 'Search cash raises',
  },
  cash_raise_schedule: {
    sormEntity: 'CashRaiseSchedule',
    label: 'Cash Raise Schedules',
    searchPlaceholder: 'Search cash raise schedules',
  },
  strategy: {
    sormEntity: 'Strategy',
    label: 'Strategies',
    searchPlaceholder: 'Search strategy name or ID',
  },
  wash_sale_restriction: {
    sormEntity: 'WashSaleRestriction',
    label: 'Wash Sale Restrictions',
    searchPlaceholder: 'Search wash sale restrictions',
  },
  account_security_replacement: {
    sormEntity: 'AccountSecurityReplacement',
    label: 'Security Replacements',
    searchPlaceholder: 'Search security replacements',
  },
  asset_allocation_template: {
    sormEntity: 'AssetAllocationTemplate',
    label: 'Allocation Templates',
    searchPlaceholder: 'Search template name',
  },
  account_group: {
    sormEntity: 'AccountGroup',
    label: 'Account Groups',
    searchPlaceholder: 'Search group name or ID',
  },
  team: {
    sormEntity: 'Team',
    label: 'Teams',
    searchPlaceholder: 'Search team name',
  },
  trading_group: {
    sormEntity: 'TradingGroup',
    label: 'Trading Groups',
    searchPlaceholder: 'Search trading group name or ID',
  },
  user: {
    sormEntity: 'User',
    label: 'Users',
    searchPlaceholder: 'Search name or email',
  },
  new_issue: {
    sormEntity: 'NewIssue',
    label: 'New Issues',
    searchPlaceholder: 'Search name, ISIN, or CUSIP',
  },
  offering: {
    sormEntity: 'Offering',
    label: 'Offerings',
    searchPlaceholder: 'Search name, ISIN, or CUSIP',
  },
  lesson_completion: {
    sormEntity: 'LessonCompletion',
    label: 'Lesson Completions',
    searchPlaceholder: 'Search lesson completions',
  },
  quiz_attempt: {
    sormEntity: 'QuizAttempt',
    label: 'Quiz Attempts',
    searchPlaceholder: 'Search course, module, or lesson',
  },
  course_requirement: {
    sormEntity: 'CourseRequirement',
    label: 'Course Requirements',
    searchPlaceholder: 'Search requirement or course',
  },
  course_requirement_progress: {
    sormEntity: 'CourseRequirementProgress',
    label: 'Course Requirement Progress',
    searchPlaceholder: 'Search user or course',
  },
  workflow_session: {
    sormEntity: 'WorkflowSession',
    label: 'Workflows',
    searchPlaceholder: 'Search title or workflow',
  },
  entity_event: {
    sormEntity: 'EntityEvent',
    label: 'Events',
    searchPlaceholder: 'Search by ID',
  },
} as const satisfies Record<string, ViewsEntityDefinition>

/** URL `?entity=` param of every Views entity, in switcher order. */
export type ViewsEntityParam = keyof typeof VIEWS_ENTITIES

/** SORM root entity of every Views entity. */
export type ViewsSormEntityName =
  (typeof VIEWS_ENTITIES)[ViewsEntityParam]['sormEntity']

export const VIEWS_ENTITY_PARAMS = Object.keys(
  VIEWS_ENTITIES,
) as ViewsEntityParam[]

const VIEWS_ENTITY_PARAM_BY_SORM_ENTITY = new Map<string, ViewsEntityParam>(
  VIEWS_ENTITY_PARAMS.map((param) => [VIEWS_ENTITIES[param].sormEntity, param]),
)

export function isViewsEntityParam(
  value: string | undefined,
): value is ViewsEntityParam {
  return value != null && value in VIEWS_ENTITIES
}

/**
 * URL entity param for a SORM entity name or a saved view's `root_entity`.
 * Undefined when Views has no grid for that entity, so callers must not offer
 * it rather than navigate to a param nothing recognizes.
 */
export function rootEntityToViewParam(
  rootEntity: string,
): ViewsEntityParam | undefined {
  return VIEWS_ENTITY_PARAM_BY_SORM_ENTITY.get(rootEntity)
}

/**
 * SORM root entity for a URL entity param, for the schema and query APIs.
 * Undefined for a param that is not a Views entity.
 */
export function viewParamToRootEntity(
  viewParam: string,
): ViewsSormEntityName | undefined {
  return isViewsEntityParam(viewParam)
    ? VIEWS_ENTITIES[viewParam].sormEntity
    : undefined
}

export function viewsEntityLabel(param: ViewsEntityParam): string {
  return VIEWS_ENTITIES[param].label
}
